import type { SstkVerifyElement } from '@sstk/verify-widget';
import { withVerificationDeadline } from '@sstk/verify-widget/deadline';
// Soulstack Verify integration (@sstk/verify-widget). This module owns the
// browser side: it builds the <sstk-verify> challenge widget, the user (or, for
// the invisible proof-of-work tier, the browser) solves it, and the resulting
// ONE-USE token travels to studio-api in the X-Captcha-Token header, with the
// widget's site key in X-Captcha-Site-Key, where it is redeemed against the
// Verify service.
//
// Every surface names its own site key and states its own reason to the user:
//   - sk_studio_publish  publish and wiki (VITE_CAPTCHA_SITE_KEY, see below)
//   - sk_studio_auth     Cognito sign in, sign up, reset, resend, and the play
//                        origin's guest relay signup
//   - sk_studio_contact  the contact form
// The auth and contact keys are constants, never read from the environment, so
// a missing env var cannot silently switch those gates off. Only the publish
// and wiki gate honours "unset means off" (requestPublishCaptchaToken), so a
// local dev build matches the API's empty-secret development state; a production
// build cannot omit it (config/requiredEnv.ts).

export const SITE_KEY_AUTH = 'sk_studio_auth';
export const SITE_KEY_CONTACT = 'sk_studio_contact';

const CAPTCHA_API_URL =
  import.meta.env.VITE_CAPTCHA_API_URL ||
  'https://europe-west1-soulstack-prod.cloudfunctions.net';
const PUBLISH_SITE_KEY = import.meta.env.VITE_CAPTCHA_SITE_KEY || '';

/** True when the publish and wiki gate is configured (see the header note). */
export function captchaEnabled(): boolean {
  return Boolean(PUBLISH_SITE_KEY);
}

interface SstkVerifiedDetail {
  token: string;
  expiresAt: number;
}

/** The user dismissed the challenge (Escape, backdrop or Cancel). */
export class CaptchaCancelledError extends Error {
  constructor() {
    super('Verification cancelled.');
    this.name = 'CaptchaCancelledError';
  }
}

/** Loads the widget's custom element definition. It extends HTMLElement, so it
 * must only ever be imported client side, inside an effect or an event. */
export function loadVerifyWidget(): Promise<unknown> {
  return withVerificationDeadline(() => import('@sstk/verify-widget'), 10_000);
}

/**
 * Build a themed inline widget. The host explicitly starts modal checks;
 * forms use the shared field interaction and submission contract.
 */
export function createVerifyElement(siteKey: string) {
  const widget = document.createElement('sstk-verify') as SstkVerifyElement;
  widget.setAttribute('site-key', siteKey);
  widget.setAttribute('action', 'auth.preflight');
  widget.setAttribute('api-url', CAPTCHA_API_URL);
  // Follow Studio's own live theme rather than a fixed dark widget, per
  // data-theme in theme.tsx (dark is the brand default; either can be active).
  const studioTheme = document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
  widget.setAttribute('theme', studioTheme);
  widget.setAttribute('auto-load', 'false');
  // Custom properties cascade into the widget's shadow root; they must be set
  // with setProperty, an assignment to style['--x'] is silently ignored.
  const brand: Record<string, string> = {
    '--sstk-accent': 'var(--ss-accent)',
    '--sstk-accent-contrast': 'var(--ss-accent-ink)',
    '--sstk-bg': 'var(--ss-panel)',
    '--sstk-fg': 'var(--ss-text)',
    '--sstk-border': 'var(--ss-border)',
  };
  for (const [name, value] of Object.entries(brand)) widget.style.setProperty(name, value);
  return widget;
}

interface CaptchaRequest {
  /** The Verify site key this token must be issued for. Required: there is no
   * environment fallback, an empty key is refused rather than skipped. */
  siteKey: string;
  /** Tells the user why they are being asked. */
  title: string;
  /** 'center' (default) is a modal with a backdrop. 'corner' is a small card
   * in the bottom-right with no backdrop, for a check that should not block
   * what the user is doing (the guest relay signup during play). */
  placement?: 'center' | 'corner';
}

/**
 * Obtain a fresh one-use Verify token by overlaying the challenge widget.
 * Resolves with the token once the user solves it; rejects with
 * CaptchaCancelledError when they dismiss the overlay (Escape, backdrop or
 * Cancel). Tokens are consumed on redemption, so callers must request a new one
 * for every gated action. Fails closed: an empty site key rejects.
 */
export async function requestCaptchaToken({
  siteKey,
  title: titleText,
  placement = 'center',
}: CaptchaRequest): Promise<string> {
  if (!siteKey) throw new Error('Verification is not configured.');
  await loadVerifyWidget();

  return new Promise<string>((resolve, reject) => {
    const restoreFocus = document.activeElement as HTMLElement | null;
    const overlay = document.createElement('div');
    overlay.dataset.sstkCaptchaOverlay = 'true';
    const corner = placement === 'corner';
    Object.assign(
      overlay.style,
      corner
        ? { position: 'fixed', right: '16px', bottom: '16px', zIndex: '200' }
        : {
            position: 'fixed',
            inset: '0',
            zIndex: '200',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.6)',
          },
    );

    const panel = document.createElement('div');
    Object.assign(panel.style, {
      background: 'var(--ss-panel)',
      border: '1px solid var(--ss-border)',
      borderRadius: '16px',
      boxShadow: '0 30px 80px rgba(0,0,0,0.7)',
      padding: '22px',
      display: 'flex',
      flexDirection: 'column',
      gap: '14px',
      width: 'min(360px, calc(100vw - 32px))',
    });

    panel.setAttribute('role', corner ? 'region' : 'dialog');
    if (!corner) panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-label', titleText);
    panel.tabIndex = -1;
    const title = document.createElement('span');
    title.textContent = titleText;
    Object.assign(title.style, { fontSize: '15px', fontWeight: '600' });

    const widget = createVerifyElement(siteKey);
    widget.setAttribute('presentation', 'dialog');

    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'ss-btn ss-btn--ghost';
    cancel.textContent = 'Cancel';

    function cleanup() {
      document.removeEventListener('keydown', onKeyDown, true);
      overlay.remove();
      if (!corner && restoreFocus?.isConnected) restoreFocus.focus();
    }
    function dismiss() {
      cleanup();
      reject(new CaptchaCancelledError());
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Tab') {
        const focusable: HTMLElement[] = [];
        const visit = (root: Element | ShadowRoot) => {
          for (const child of root.children) {
            if (child instanceof HTMLElement && child.matches('button:not([disabled]),input:not([disabled]),[tabindex="0"]') && !child.hidden) focusable.push(child);
            if (child.shadowRoot) visit(child.shadowRoot);
            visit(child);
          }
        };
        visit(panel);
        const first = focusable[0], last = focusable[focusable.length - 1];
        const active = e.composedPath()[0];
        if (e.shiftKey && (active === first || !e.composedPath().includes(panel))) { e.preventDefault(); last?.focus(); }
        else if (!e.shiftKey && (active === last || !e.composedPath().includes(panel))) { e.preventDefault(); first?.focus(); }
      }
      if (e.key === 'Escape') {
        e.stopPropagation();
        dismiss();
      }
    }

    widget.addEventListener('sstk-verified', (event) => {
      const detail = (event as CustomEvent<SstkVerifiedDetail>).detail;
      if (!detail.token || !(detail.expiresAt > Date.now())) {
        widget.refresh();
        return;
      }
      cleanup();
      resolve(detail.token);
    });
    cancel.addEventListener('click', dismiss);
    if (!corner) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) dismiss();
      });
      document.addEventListener('keydown', onKeyDown, true);
    }

    panel.append(title, widget, cancel);
    overlay.append(panel);
    document.body.append(overlay);
    if (!corner) cancel.focus();
    widget.addEventListener('sstk-state', (event) => {
      if (!corner && (event as CustomEvent<{state: string}>).detail.state === 'presenting') widget.focusChallenge();
    });
    widget.start();
  });
}

/**
 * The publish and wiki gate. Resolves '' without showing anything when
 * VITE_CAPTCHA_SITE_KEY is unset (development only, see the header note), which
 * mirrors the API's empty-secret disabled state. Callers should only ask for a
 * token when the API will actually redeem one.
 */
export function requestPublishCaptchaToken(): Promise<string> {
  if (!captchaEnabled()) return Promise.resolve('');
  return requestCaptchaToken({
    siteKey: PUBLISH_SITE_KEY,
    title: 'Quick check before we update your game',
  });
}

/** Headers for a gated API call. With no `siteKey` (publish and wiki) only the
 * token is sent, which the API treats as sk_studio_publish; empty when there is
 * no token (captcha disabled in development). */
export function captchaHeader(token: string, siteKey?: string): Record<string, string> {
  if (!token) return {};
  return siteKey
    ? { 'X-Captcha-Token': token, 'X-Captcha-Site-Key': siteKey }
    : { 'X-Captcha-Token': token };
}
